<script setup lang="ts" generic="P extends unknown[] = never[]">
import { VueWebOtp } from 'vue-web-otp';
import LoginStepWrapper from '@components/layout/auth/LoginStepWrapper.vue';

const { error, loader, loading } = defineProps<{
    error?: string;
    loader: (...params: P) => Promise<void>;
    loading?: boolean;
}>();

const emit = defineEmits<{
    submit: [];
    error: [];
}>();

const {
    loading: otpLoading,
    timer,
    sendToken,
    tooManyTries,
} = useOtp<P>(loader);

const startToken = ref<HTMLInputElement>();
const tokenRef = ref<HTMLInputElement[]>([]);
const localTimer = ref(120);
const resendEnabled = ref(false);
const tokenValue = ref<string[]>([]);
const token = defineModel<string>('token', { default: '' });
watch(
    tokenValue,
    value => {
        token.value = value.map(val => val.toString()).join('');
    },
    { deep: true }
);
watch(
    () => error,
    () => {
        if (error) {
            tokenValue.value = [];
            token.value = '';
            startToken.value?.focus();
        }
    }
);
const inputs = computed(() => {
    if (!startToken.value || !tokenRef.value) {
        return [];
    }
    return [startToken.value, ...tokenRef.value];
});
const timerError = computed(() =>
    localTimer.value > 10 ? 'var(--cool-gray-700)' : 'var(--red-400)'
);
const setTimer = () => {
    localTimer.value--;
    if (localTimer.value <= 0) {
        resendEnabled.value = true;
    } else {
        window.setTimeout(setTimer, 1000);
    }
};
const inputChange = (index: number, ev: Event) => {
    const enteredValue = (ev.target as HTMLInputElement).value;
    if (!ev || !ev.target) {
        return;
    }
    if (enteredValue) {
        if (enteredValue.length === inputs.value.length) {
            handleOtp(enteredValue);
        }
        if (inputs.value[index + 1]) {
            inputs.value[index + 1]?.focus();
        }
    } else if (inputs.value[index - 1]) {
        inputs.value[index - 1]?.focus();
    }
};
const resend = async (...params: P) => {
    resendEnabled.value = false;
    try {
        await sendToken(...params);
        localTimer.value = timer.value;
        setTimer();
        window.OtpChannel?.postMessage('start');
    } catch (e) {
        reportException(e);
    }
};
const handleOtp = (event: string) => {
    tokenValue.value = event.split('');
};
watch(token, val => {
    if (val.length === 6) {
        emit('submit');
    }
});
onMounted(async () => {
    await (sendToken as any)();
    localTimer.value = timer.value;
    setTimer();
    inputs.value[0].focus();
    window.HandleOtp = handleOtp;
    window.OtpChannel?.postMessage('start');
});
onUnmounted(() => {
    window.HandleOtp = undefined;
    window.OtpChannel?.postMessage('finish');
});
watch(tooManyTries, value => {
    if (value) {
        emit('error');
    }
});
</script>

<template>
    <LoginStepWrapper title="کد تایید را وارد کنید">
        <template #extra-header-item>
            <slot name="extra-header-item" />
        </template>
        <template #inputs>
            <div class="otp-container">
                <div class="inputs-container">
                    <div class="otp-token-container" dir="ltr">
                        <VueWebOtp @input="handleOtp">
                            <template #default="{ autocomplete }">
                                <input
                                    ref="startToken"
                                    :key="0"
                                    v-model="tokenValue[0]"
                                    maxlength="1"
                                    type="number"
                                    class="otp-token-input"
                                    :autocomplete
                                    :class="{
                                        active: !!tokenValue[0]?.toString(),
                                        error: !!error && !tokenValue.length,
                                    }"
                                    @input="inputChange(0, $event)"
                                    @focus="
                                        (
                                            $event.target as HTMLInputElement
                                        ).select()
                                    "
                                />
                            </template>
                        </VueWebOtp>
                        <input
                            v-for="count in 5"
                            ref="tokenRef"
                            :key="count"
                            v-model="tokenValue[count]"
                            maxlength="1"
                            type="number"
                            class="otp-token-input"
                            :class="{
                                active: !!tokenValue[count]?.toString(),
                                error: !!error && !tokenValue.length,
                            }"
                            @input="inputChange(count, $event)"
                            @focus="
                                ($event.target as HTMLInputElement).select()
                            "
                        />
                    </div>
                    <mr-typography size="small" color="var(--red-400)">
                        {{ error }}
                    </mr-typography>
                </div>
                <slot name="tokens-append" />
            </div>
        </template>
        <mr-loading v-show="loading || otpLoading" :scale="0.75" />
        <template v-if="!otpLoading" #actions>
            <mr-typography v-if="!resendEnabled" color="var(--cool-gray-700)">
                ارسال مجدد کد تا
                <mr-typography type="span" :color="timerError">
                    {{ localTimer }}
                </mr-typography>
                ثانیه دیگر
            </mr-typography>
            <template v-else>
                <slot name="resend" :resend />
            </template>
        </template>
    </LoginStepWrapper>
</template>

<style scoped lang="scss">
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
}

.otp-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    .inputs-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        .otp-token-container {
            display: flex;
            align-items: center;
            gap: 13px;
            .otp-token-input {
                height: 48px;
                border-radius: 8px;
                flex-basis: calc((100% - 65px) / 6);
                max-width: calc((100% - 65px) / 6);
                background-color: white;
                text-align: center;
                border: 1px solid usePalette($p-gray, $p-400);
                &.active {
                    border: 1px solid usePalette();
                }
                &.error {
                    border: 1px solid usePalette($p-red, $p-400) !important;
                }
            }
            .otp-token-input:focus {
                border: 1px solid usePalette();
            }
        }
    }
}
</style>
